import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  adjustQuantityToDeal,
  buildProductSelection,
  findEquivalentDeal,
  normalizeQuantityForDeal
} from '../domain/ProductSelection.model';
import {DealType} from '../domain/DealSelection.model';

const DEFAULT_QUANTITY = 1;

// Deals que ya llevan su propio lote (oferta de corta caducidad) o no admiten uno: el lote elegido a
// mano por el comercial solo acompaña a la compra normal o a una oferta por cantidad.
const admiteLoteManual = (selection) =>
  selection?.dealType !== DealType.LOT && selection?.dealType !== DealType.PROMO;

export const useProductSelection = (productDetails, defaultQuantity = DEFAULT_QUANTITY) => {
  const [quantity, setRawQuantity] = useState(defaultQuantity);
  const [dealSelection, setRawDealSelection] = useState(null);
  // Lote elegido a mano en la tabla de lotes de la ficha (comercial con permiso "Elegir lote"); null = automático
  const [manualLot, setManualLot] = useState(null);
  // Trato vigente sin esperar al render, para ajustar la cantidad aunque se llame a los dos setters seguidos
  const dealRef = useRef(null);

  const setDealSelection = useCallback((deal) => {
    dealRef.current = deal;
    setRawDealSelection(deal);
    setRawQuantity(prev => normalizeQuantityForDeal(deal, prev));
  }, []);

  const setQuantity = useCallback((value) => {
    setRawQuantity(prev => adjustQuantityToDeal(dealRef.current, prev, value));
  }, []);

  // Ficha recargada: el trato elegido apunta a objetos viejos
  useEffect(() => {
    const deal = dealRef.current;
    if (deal && findEquivalentDeal(productDetails, deal) !== deal) {
      setDealSelection(findEquivalentDeal(productDetails, deal));
    }
  }, [productDetails, setDealSelection]);

  const productSelection = useMemo(() => {
    const selection = buildProductSelection(productDetails, dealSelection, quantity);
    if (manualLot && admiteLoteManual(selection)) {
      selection.lotCode = manualLot;
    }
    return selection;
  }, [productDetails, dealSelection, quantity, manualLot]);

  return { productSelection, dealSelection, setQuantity, setDealSelection, manualLot, setManualLot };
};
